import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .at * { box-sizing:border-box; }
  .at { font-family:'Sora',sans-serif; color:#1a1d2e; animation:atFade .4s ease both; }
  @keyframes atFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .at-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .at-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .at-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-green { background:#10c98e; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .info-box { background:#eef2ff; border:1px solid #c7d2fe; border-radius:12px; padding:12px 16px; margin-bottom:1.4rem; font-size:.78rem; color:#3730a3; line-height:1.6; }
  .info-box strong { font-weight:700; }

  .types-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:1rem; margin-bottom:1.4rem; }
  .type-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; display:flex; align-items:center; justify-content:space-between; gap:10px; transition:all .15s; }
  .type-card:hover { border-color:#3b6ef8; box-shadow:0 4px 14px rgba(59,110,248,.08); }
  .type-card.inactive { opacity:.5; }
  .type-info { display:flex; align-items:center; gap:10px; }
  .type-order { width:26px; height:26px; border-radius:7px; background:#f0f2f7; color:#6b7194; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
  .type-name  { font-size:.86rem; font-weight:600; color:#0f1123; }
  .type-status{ font-size:.67rem; color:#9094a8; margin-top:2px; }
  .type-actions { display:flex; gap:6px; flex-shrink:0; }

  .at-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .at-loading { display:flex; align-items:center; justify-content:center; height:150px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:420px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus { border-color:#3b6ef8; background:white; }

  @media (max-width:768px) {
    .at-title { font-size:1.1rem; }
    .at-sub   { font-size:.72rem; }
    .at-header { margin-bottom:1rem; }
    .types-grid { grid-template-columns:1fr; }
    .info-box { font-size:.74rem; }
    .modal { padding:18px; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }

  @media (max-width:400px) {
    .type-actions .btn-sm { padding:5px 7px; }
  }
`;

const AssessmentTypes = () => {
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ name:'', order:'0' });
  const [assessmentMode, setAssessmentMode] = useState('assessment');
  const isTermMode = assessmentMode === 'term';

  useEffect(() => {
    api.get('/admin/settings/assessment-mode')
      .then(({ data }) => {
        const mode = data.assessmentMode || 'assessment';
        setAssessmentMode(mode);
        fetchTypes(mode);
      })
      .catch(() => fetchTypes('assessment'));
  }, []);

  const fetchTypes = async (mode = assessmentMode) => {
    try {
      setLoading(true);
      const kindParam = mode === 'term' ? '?kind=term_component' : '';
      const { data } = await api.get(`/admin/assessment-types${kindParam}`);
      setTypes(data);
    } catch { toast.error('Failed to load'); }
    finally  { setLoading(false); }
  };

  const openAdd  = () => { setEditing(null); setForm({ name:'', order: String(types.length) }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, order: String(t.order) }); setOpen(true); };

  const handleSeed = async () => {
    try {
      const { data } = await api.post('/admin/assessment-types/seed');
      if (data.seeded) { toast.success('Default types added'); fetchTypes(); }
      else toast('Types already exist');
    } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      setSaving(true);
      const body = isTermMode
        ? { name: form.name, order: Number(form.order), kind: 'term_component' }
        : { name: form.name, order: Number(form.order) };
      if (editing) {
        await api.put(`/admin/assessment-types/${editing._id}`, { name: form.name, order: Number(form.order) });
        toast.success('Updated');
      } else {
        await api.post('/admin/assessment-types', body);
        toast.success('Added');
      }
      setOpen(false);
      fetchTypes();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (t) => {
    try {
      await api.put(`/admin/assessment-types/${t._id}`, { active: !t.active });
      fetchTypes();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="at">
      <style>{style}</style>

      <div className="at-header">
        <div>
          <div className="at-title">{isTermMode ? 'Term Components' : 'Assessment Types'}</div>
          <div className="at-sub">
            {isTermMode
              ? 'Manage the reusable components used to grade each term — Test, Oral/Viva, Class Work etc.'
              : 'Manage the types that appear in grade reports — Day to Day, EOT, Oral Work etc.'}
          </div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {!isTermMode && types.length === 0 && (
            <button className="btn btn-ghost" onClick={handleSeed}>Seed Defaults</button>
          )}
          <button className="btn btn-blue" onClick={openAdd}><PlusIcon />{isTermMode ? 'Add Component' : 'Add Type'}</button>
        </div>
      </div>

      <div className="info-box">
        {isTermMode ? (
          <>
            <strong>How this works:</strong> Each component you add here can be graded within any term.
            Teachers select from these components when entering marks for a term.
            Use <strong>Order</strong> to control the column sequence.
            Inactive components won't appear in the teacher dropdown.
          </>
        ) : (
          <>
            <strong>How this works:</strong> Each type you add here becomes a column in the grade report.
            Teachers select from these types when creating an assessment.
            Use <strong>Order</strong> to control the column sequence in the report.
            Inactive types won't appear in the teacher dropdown.
          </>
        )}
      </div>

      {loading ? (
        <div className="at-loading"><div className="spinner"/>Loading…</div>
      ) : types.length === 0 ? (
        <div className="at-empty">
          {isTermMode
            ? 'No term components yet. Add one — e.g. "Test" or "Oral/Viva".'
            : 'No assessment types yet. Add one or click "Seed Defaults" to add the standard set.'}
        </div>
      ) : (
        <div className="types-grid">
          {types.map(t => (
            <div key={t._id} className={`type-card ${!t.active ? 'inactive' : ''}`}>
              <div className="type-info">
                <div className="type-order">{t.order}</div>
                <div>
                  <div className="type-name">{t.name}</div>
                  <div className="type-status">{t.active ? 'Active' : 'Inactive'}</div>
                </div>
              </div>
              <div className="type-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(t)} title={t.active ? 'Deactivate' : 'Activate'}>
                  {t.active ? '●' : '○'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><PencilSquareIcon /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{editing ? (isTermMode ? 'Edit Component' : 'Edit Type') : (isTermMode ? 'Add Term Component' : 'Add Assessment Type')}</div>
              <button className="modal-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="form-field">
              <label className="form-label">Name *</label>
              <input type="text" className="form-input" value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder={isTermMode ? 'e.g. Test, Oral/Viva, Class Work…' : 'e.g. Day to Day, EOT, Oral Work…'} autoFocus />
            </div>
            <div className="form-field">
              <label className="form-label">Order (column position in grade report)</label>
              <input type="number" className="form-input" value={form.order} min="0"
                onChange={e => setForm({...form, order: e.target.value})} />
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default AssessmentTypes;